import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <img
              src="https://static.readdy.ai/image/0fc33fa68f46f743570ad8ba1c3172ea/8b0c947e15fa0f478d2b95b378bf5579.png"
              alt="Regenerative Leads"
              className="h-10 w-auto"
            />
          </div>
          <div className="flex items-center gap-8">
            <a
              href="#how-it-works"
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                scrolled ? 'text-gray-700 hover:text-black' : 'text-white hover:text-gray-200'
              }`}
            >
              How It Works
            </a>
            <a
              href="#industries"
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                scrolled ? 'text-gray-700 hover:text-black' : 'text-white hover:text-gray-200'
              }`}
            >
              Industries
            </a>
            <a
              href="#cta"
              className="bg-black text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-900 transition-colors whitespace-nowrap cursor-pointer"
            >
              Book a Call
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/b2d8e3f37215f3b0eb796ebd8962284c.jpg"
            alt="Hero Background"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/40"></div>
        </div>

        {/* Hero Content */}
        <div className="relative max-w-7xl mx-auto px-6 py-32 w-full">
          <div className="max-w-4xl">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-teal-500/20 to-cyan-500/20 border border-teal-400/30 mb-8">
              <span className="text-sm font-medium text-white">AI-Powered Lead Systems</span>
            </div>
            
            <h1 className="text-6xl md:text-7xl font-bold text-white mb-6 leading-tight">
              Lead systems that grow<br />
              <span className="ml-8">back stronger.</span>
            </h1>
            
            <p className="text-xl text-gray-200 mb-10 max-w-2xl leading-relaxed">
              Regenerative Leads builds AI-powered lead engines that continuously attract, qualify, and convert high-value customers for service and professional businesses.
            </p>
            
            <div className="flex flex-wrap gap-4">
              <a
                href="#cta"
                className="bg-black text-white px-8 py-4 rounded-lg text-base font-semibold hover:bg-gray-900 transition-all hover:scale-105 flex items-center gap-2 whitespace-nowrap cursor-pointer"
              >
                Book a Strategy Call
                <i className="ri-arrow-right-line text-lg"></i>
              </a>
              <a
                href="#how-it-works"
                className="bg-transparent text-white px-8 py-4 rounded-lg text-base font-semibold border-2 border-white hover:bg-white hover:text-black transition-all whitespace-nowrap cursor-pointer"
              >
                See How It Works
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="bg-gray-50 py-6 border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap items-center justify-center gap-3 text-sm text-gray-600">
            <span className="font-medium">Trusted by modern operators in</span>
            <div className="flex items-center gap-3">
              <span className="font-semibold text-gray-800">Home Services</span>
              <span className="text-gray-300">•</span>
              <span className="font-semibold text-gray-800">Legal</span>
              <span className="text-gray-300">•</span>
              <span className="font-semibold text-gray-800">Financial</span>
              <span className="text-gray-300">•</span>
              <span className="font-semibold text-gray-800">Insurance</span>
              <span className="text-gray-300">•</span>
              <span className="font-semibold text-gray-800">Dental</span>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-5xl font-bold text-black mb-4">
                The problem with traditional lead generation
              </h2>
              <div className="w-20 h-1 bg-red-500 mb-8"></div>
              <div className="space-y-6">
                <p className="text-xl text-gray-700 leading-relaxed">
                  Most lead generation stops working the moment campaigns stop.
                </p>
                <p className="text-xl text-gray-700 leading-relaxed">
                  Manual follow-ups break. Leads go cold. Opportunities slip through the cracks.
                </p>
                <p className="text-xl text-gray-700 leading-relaxed">
                  Growth becomes unpredictable.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="w-full h-96 rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20business%20person%20looking%20frustrated%20at%20declining%20sales%20chart%20and%20broken%20funnel%20visualization%20on%20computer%20screen%2C%20modern%20office%20environment%20with%20clean%20desk%20setup%2C%20realistic%20corporate%20photography%20style%20with%20natural%20lighting%2C%20showing%20stress%20and%20concern%20about%20failed%20marketing%20campaigns%20and%20lost%20opportunities%2C%20high%20quality%20business%20stock%20photo%20aesthetic&width=800&height=600&seq=problem-visual-001&orientation=landscape"
                  alt="Traditional Lead Generation Problems"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-32 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-teal-100/40 to-cyan-100/40 rounded-full blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="relative order-2 md:order-1">
              <div className="w-full h-96 rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://readdy.ai/api/search-image?query=Futuristic%20AI%20automation%20system%20with%20interconnected%20nodes%20and%20flowing%20data%20streams%2C%20glowing%20teal%20and%20cyan%20circuit%20patterns%20forming%20a%20regenerative%20loop%2C%20modern%20technology%20visualization%20showing%20continuous%20growth%20and%20self-improving%20systems%2C%20clean%20minimalist%20design%20with%20geometric%20shapes%20and%20neural%20network%20architecture%2C%20professional%20tech%20illustration%20with%20depth%20and%20dimension&width=800&height=600&seq=solution-visual-001&orientation=landscape"
                  alt="Regenerative AI Systems"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
            
            <div className="order-1 md:order-2">
              <h2 className="text-6xl font-bold text-black mb-8">
                The regenerative approach
              </h2>
              
              <p className="text-2xl font-semibold text-gray-900 mb-6">
                We don't run campaigns. We build systems.
              </p>
              
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Our AI-driven lead infrastructure continuously regenerates demand, improves qualification, and converts opportunities automatically —
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                without constant manual effort.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-5xl font-bold text-black text-center mb-20">
            How It Works
          </h2>
          
          <div className="grid md:grid-cols-4 gap-8">
            {/* Step 1 */}
            <div className="bg-white rounded-2xl p-10 hover:shadow-xl transition-all duration-300 border border-gray-100 group">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-radar-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-black mb-4">Attract</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Multi-channel inbound and outbound systems designed for high-intent demand.
              </p>
            </div>

            {/* Step 2 */}
            <div className="bg-white rounded-2xl p-10 hover:shadow-xl transition-all duration-300 border border-gray-100 group">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-cyan-400 to-teal-500 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-filter-3-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-black mb-4">Qualify</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                AI agents filter, score, and route leads in real time.
              </p>
            </div>

            {/* Step 3 */}
            <div className="bg-white rounded-2xl p-10 hover:shadow-xl transition-all duration-300 border border-gray-100 group">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-check-double-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-black mb-4">Convert</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Automated booking, follow-ups, and reminders close faster.
              </p>
            </div>

            {/* Step 4 */}
            <div className="bg-white rounded-2xl p-10 hover:shadow-xl transition-all duration-300 border border-gray-100 group">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-400 to-teal-600 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-loop-right-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-black mb-4">Regenerate</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Every interaction improves the system, compounding results over time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Industries Served Section */}
      <section id="industries" className="py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-gray-200 mb-4">
              <span className="text-sm font-semibold text-gray-700">Built for high-value industries</span>
            </div>
            <h2 className="text-5xl font-bold text-black">
              Who we serve
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {/* Home Services */}
            <div className="bg-gradient-to-br from-white to-teal-50 rounded-xl overflow-hidden border border-gray-200 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer">
              <div className="w-full h-48">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20HVAC%20technician%20working%20on%20modern%20air%20conditioning%20system%20in%20clean%20residential%20setting%2C%20tools%20and%20equipment%20visible%2C%20bright%20natural%20lighting%2C%20high%20quality%20service%20industry%20photography%20showing%20expertise%20and%20professionalism%2C%20simple%20clean%20background%20emphasizing%20the%20work%2C%20realistic%20corporate%20style%20photo&width=600&height=400&seq=industry-home-001&orientation=landscape"
                  alt="Home Services"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-8">
                <div className="w-12 h-12 flex items-center justify-center mb-5">
                  <i className="ri-tools-line text-4xl text-teal-600"></i>
                </div>
                <h3 className="text-xl font-bold text-black mb-3">
                  Home Services (HVAC & Plumbing)
                </h3>
                <p className="text-base text-gray-600 leading-relaxed">
                  Never miss a call. Book more jobs automatically.
                </p>
              </div>
            </div>

            {/* Personal Injury Law */}
            <div className="bg-gradient-to-br from-white to-amber-50 rounded-xl overflow-hidden border border-gray-200 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer">
              <div className="w-full h-48">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20attorney%20in%20modern%20law%20office%20consulting%20with%20client%2C%20elegant%20office%20interior%20with%20law%20books%20and%20professional%20decor%2C%20confident%20legal%20professional%20in%20business%20attire%2C%20natural%20lighting%20through%20windows%2C%20high%20quality%20corporate%20photography%20showing%20trust%20and%20expertise%2C%20simple%20clean%20background%20emphasizing%20professionalism&width=600&height=400&seq=industry-legal-001&orientation=landscape"
                  alt="Personal Injury Law"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-8">
                <div className="w-12 h-12 flex items-center justify-center mb-5">
                  <i className="ri-scales-3-line text-4xl text-amber-600"></i>
                </div>
                <h3 className="text-xl font-bold text-black mb-3">
                  Personal Injury Law
                </h3>
                <p className="text-base text-gray-600 leading-relaxed">
                  High-intent intake, faster response, stronger cases.
                </p>
              </div>
            </div>

            {/* Financial Services */}
            <div className="bg-gradient-to-br from-white to-green-50 rounded-xl overflow-hidden border border-gray-200 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer">
              <div className="w-full h-48">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20financial%20advisor%20meeting%20with%20clients%20in%20modern%20office%2C%20discussing%20investment%20strategies%20with%20charts%20and%20documents%20on%20desk%2C%20confident%20business%20professional%20in%20formal%20attire%2C%20bright%20natural%20office%20lighting%2C%20high%20quality%20corporate%20photography%20showing%20expertise%20and%20trust%2C%20simple%20clean%20background%20emphasizing%20professionalism&width=600&height=400&seq=industry-finance-001&orientation=landscape"
                  alt="Financial Services"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-8">
                <div className="w-12 h-12 flex items-center justify-center mb-5">
                  <i className="ri-line-chart-line text-4xl text-green-600"></i>
                </div>
                <h3 className="text-xl font-bold text-black mb-3">
                  Financial Services & Lending
                </h3>
                <p className="text-base text-gray-600 leading-relaxed">
                  Smarter lead scoring and trust-based nurturing.
                </p>
              </div>
            </div>

            {/* Insurance */}
            <div className="bg-gradient-to-br from-white to-teal-50 rounded-xl overflow-hidden border border-gray-200 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer">
              <div className="w-full h-48">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20insurance%20agent%20consulting%20with%20family%20clients%20in%20modern%20office%2C%20reviewing%20policy%20documents%20and%20providing%20coverage%20advice%2C%20confident%20business%20professional%20in%20formal%20attire%2C%20warm%20natural%20lighting%2C%20high%20quality%20corporate%20photography%20showing%20trust%20and%20security%2C%20simple%20clean%20background%20emphasizing%20professionalism&width=600&height=400&seq=industry-insurance-001&orientation=landscape"
                  alt="Insurance Services"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-8">
                <div className="w-12 h-12 flex items-center justify-center mb-5">
                  <i className="ri-shield-check-line text-4xl text-teal-600"></i>
                </div>
                <h3 className="text-xl font-bold text-black mb-3">
                  Insurance Carriers & Brokers
                </h3>
                <p className="text-base text-gray-600 leading-relaxed">
                  Quote automation, follow-ups, and retention systems.
                </p>
              </div>
            </div>

            {/* Dental */}
            <div className="bg-gradient-to-br from-white to-cyan-50 rounded-xl overflow-hidden border border-gray-200 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer">
              <div className="w-full h-48">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20dentist%20in%20modern%20dental%20clinic%20examining%20patient%20with%20advanced%20dental%20equipment%2C%20clean%20white%20clinical%20environment%20with%20state%20of%20the%20art%20technology%2C%20friendly%20healthcare%20professional%20in%20medical%20attire%2C%20bright%20clinical%20lighting%2C%20high%20quality%20medical%20photography%20showing%20expertise%20and%20care%2C%20simple%20clean%20background%20emphasizing%20professionalism&width=600&height=400&seq=industry-dental-001&orientation=landscape"
                  alt="Dental Services"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-8">
                <div className="w-12 h-12 flex items-center justify-center mb-5">
                  <i className="ri-heart-pulse-line text-4xl text-cyan-600"></i>
                </div>
                <h3 className="text-xl font-bold text-black mb-3">
                  Dental & Clinical Practices
                </h3>
                <p className="text-base text-gray-600 leading-relaxed">
                  New patient acquisition and reactivation on autopilot.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-5xl font-bold text-black mb-16">
                Why Regenerative Leads
              </h2>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-xl text-white"></i>
                  </div>
                  <p className="text-lg font-medium text-gray-900 pt-1">
                    AI agents working 24/7
                  </p>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-xl text-white"></i>
                  </div>
                  <p className="text-lg font-medium text-gray-900 pt-1">
                    Predictable, systemized growth
                  </p>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-xl text-white"></i>
                  </div>
                  <p className="text-lg font-medium text-gray-900 pt-1">
                    Built for high-value industries
                  </p>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-xl text-white"></i>
                  </div>
                  <p className="text-lg font-medium text-gray-900 pt-1">
                    Automated follow-up and booking
                  </p>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-xl text-white"></i>
                  </div>
                  <p className="text-lg font-medium text-gray-900 pt-1">
                    Designed to scale with your business
                  </p>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="w-full h-96 rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20business%20dashboard%20showing%20upward%20growth%20metrics%20and%20analytics%2C%20clean%20interface%20with%20teal%20and%20green%20data%20visualizations%2C%20automated%20AI%20system%20indicators%20and%20performance%20charts%2C%20professional%20SaaS%20application%20screenshot%20with%20increasing%20revenue%20graphs%20and%20conversion%20metrics%2C%20minimalist%20design%20with%20clear%20positive%20trends%20and%20success%20indicators&width=800&height=600&seq=benefits-visual-001&orientation=landscape"
                  alt="Growth and Performance Metrics"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Results Focus Section */}
      <section className="py-24 bg-gradient-to-br from-gray-900 to-gray-800 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-2 h-2 bg-white rounded-full"></div>
          <div className="absolute top-32 right-20 w-2 h-2 bg-white rounded-full"></div>
          <div className="absolute bottom-20 left-32 w-2 h-2 bg-white rounded-full"></div>
          <div className="absolute bottom-40 right-40 w-2 h-2 bg-white rounded-full"></div>
          <div className="absolute top-1/2 left-1/4 w-2 h-2 bg-white rounded-full"></div>
          <div className="absolute top-1/3 right-1/3 w-2 h-2 bg-white rounded-full"></div>
        </div>
        
        <div className="max-w-4xl mx-auto px-6 text-center relative">
          <div className="w-32 h-1 bg-gradient-to-r from-transparent via-white to-transparent mx-auto mb-8"></div>
          
          <h2 className="text-5xl font-bold text-white mb-8">
            Built for outcomes
          </h2>
          
          <p className="text-xl text-gray-300 leading-relaxed">
            We measure success by booked calls, signed cases, issued policies, new patients, and revenue generated — not clicks or impressions.
          </p>
        </div>
      </section>

      {/* Final CTA Section */}
      <section id="cta" className="py-32 bg-gradient-to-br from-teal-500 via-cyan-500 to-teal-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMC41IiBvcGFjaXR5PSIwLjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-30"></div>
        
        <div className="max-w-3xl mx-auto px-6 text-center relative">
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Ready to build a lead system that compounds?
          </h2>
          
          <p className="text-xl text-white/90 mb-10 leading-relaxed">
            Let's design a regenerative growth engine tailored to your business.
          </p>
          
          <a
            href="#"
            className="inline-flex items-center gap-3 bg-black text-white px-12 py-5 rounded-xl text-lg font-semibold hover:bg-gray-900 hover:scale-105 transition-all shadow-2xl whitespace-nowrap cursor-pointer"
          >
            Book Your Strategy Call
            <i className="ri-arrow-right-line text-xl"></i>
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white py-16 border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center space-y-8">
            <p className="text-base text-gray-300">
              Regenerative Leads — AI-Powered Lead Systems for Modern Operators
            </p>
            
            <div className="pt-8 border-t border-gray-800">
              <p className="text-sm text-gray-500">
                © 2025 Regenerative Leads. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}